/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { OsSubType, OsType } from "~/generated/graphql";
import { dateFormat } from "~/constants/dateFormats";
import { createCountFilterDefinition } from "~/hooks/useAppliedFilters/definitionFactories";
import { AppliedFilterDefinition } from "~/hooks/useAppliedFilters/types";
import {
  getCountBasedDisplayValue,
  removeSelectedValue,
} from "~/hooks/useAppliedFilters/utils";
import { QueryState } from "~/hooks/useQueryState/useQueryState";
import { ResourceQuery } from "../types/ResourceQuery";

type DefinitionKey =
  | "dateAdded"
  | "regions"
  | "plans"
  | "cloudProviders"
  | "cloudAccounts"
  | "resourceGroups"
  | "resourcePingStatuses"
  | "lastUpdateStatuses"
  | "operatingSystems"
  | "resourceTags";
type ResourceQueryState = ResourceQuery & QueryState;

type Definition = AppliedFilterDefinition<ResourceQueryState, DefinitionKey>;

const lastUpdateStatusLabelsByValue: Record<string, string> = {
  PENDING_UPDATES: "Pending Updates",
  FAILED: "Action Failed",
  NO_UPDATES_DATA: "No Updates Data",
  IN_PROGRESS: "Action in Progress",
  NO_PENDING_UPDATES: "No Pending Updates",
};

const operatingSystemLabelsByValue: Record<string, string> = {
  [OsType.Windows]: "Windows",
  [OsType.Linux]: "Linux",
  [OsSubType.WindowsServer]: "Windows Server",
  [OsSubType.Amazon]: "Amazon Linux",
  [OsSubType.Centos]: "CentOS",
  [OsSubType.Debian]: "Debian",
  [OsSubType.OracleLinux]: "Oracle Linux",
  [OsSubType.Redhat]: "Red Hat",
  [OsSubType.Suse]: "Suse",
  [OsSubType.Ubuntu]: "Ubuntu",
};

const getPlanDisplayValue = (
  values: string[],
  isExcluded: boolean | undefined,
  planNamesById: Record<string, string>
) => {
  if (values.length === 1) {
    const label = planNamesById[values[0]] ?? values[0];
    return isExcluded ? `Exclude: ${label}` : label;
  }

  return getCountBasedDisplayValue(values, isExcluded, {
    singular: "Plan",
    plural: "Plans",
  });
};

export const createResourcesAppliedFilters = (
  planNamesById: Record<string, string> = {},
  cloudAccountNamesById: Record<string, string> = {},
  resourceGroupNamesById: Record<string, string> = {}
): Definition[] => [
  {
    key: "dateAdded",
    label: "Date Added",
    enableValuePopover: false,
    getValues: (queryState) =>
      queryState.dateAddedStartUTC && queryState.dateAddedEndUTC
        ? [queryState.dateAddedStartUTC, queryState.dateAddedEndUTC]
        : [],
    getValueDisplay: (queryState) =>
      `${dayjs(queryState.dateAddedStartUTC).format(
        dateFormat.shortDate
      )} - ${dayjs(queryState.dateAddedEndUTC).format(dateFormat.shortDate)}`,
    clear: () => ({
      dateAddedStartUTC: undefined,
      dateAddedEndUTC: undefined,
    }),
  },
  createCountFilterDefinition<ResourceQueryState, "regions">({
    key: "regions",
    label: "Region",
    labels: { singular: "Region", plural: "Regions" },
    getValues: (queryState) => queryState.regions,
    getExcluded: (queryState) => queryState.excludeRegions,
    clear: () => ({ regions: undefined, excludeRegions: undefined }),
    applyValuesPatch: (queryState, values) => ({
      regions: values.length ? values : undefined,
      excludeRegions: values.length ? queryState.excludeRegions : undefined,
    }),
  }),
  {
    key: "plans",
    label: "Plan",
    getValues: (queryState) => queryState.plans ?? [],
    getValueLabel: (value) => planNamesById[value] ?? value,
    getValueDisplay: (queryState, values) =>
      getPlanDisplayValue(values, queryState.excludePlans, planNamesById),
    clear: () => ({ plans: undefined, excludePlans: undefined }),
    removeValue: (queryState, valueToRemove) => {
      const nextPlans = removeSelectedValue(queryState.plans, valueToRemove);
      return {
        plans: nextPlans.length ? nextPlans : undefined,
        excludePlans:
          nextPlans.length > 0 ? queryState.excludePlans : undefined,
      };
    },
  },
  createCountFilterDefinition<ResourceQueryState, "cloudProviders">({
    key: "cloudProviders",
    label: "Cloud Provider",
    labels: { singular: "Cloud Provider", plural: "Cloud Providers" },
    getValues: (queryState) => queryState.cloudProviders,
    getExcluded: (queryState) => queryState.excludeCloudProviders,
    clear: () => ({
      cloudProviders: undefined,
      excludeCloudProviders: undefined,
    }),
    applyValuesPatch: (queryState, values) => ({
      cloudProviders: values.length ? values : undefined,
      excludeCloudProviders: values.length
        ? queryState.excludeCloudProviders
        : undefined,
    }),
  }),
  {
    key: "cloudAccounts",
    label: "Cloud Account",
    getValues: (queryState) => queryState.cloudAccounts ?? [],
    getValueLabel: (value) => cloudAccountNamesById[value] ?? value,
    getValueDisplay: (queryState, values) => {
      if (values.length === 1) {
        const label = cloudAccountNamesById[values[0]] ?? values[0];
        return queryState.excludeCloudAccounts ? `Exclude: ${label}` : label;
      }

      return getCountBasedDisplayValue(
        values,
        queryState.excludeCloudAccounts,
        {
          singular: "Cloud Account",
          plural: "Cloud Accounts",
        }
      );
    },
    clear: () => ({
      cloudAccounts: undefined,
      excludeCloudAccounts: undefined,
    }),
    removeValue: (queryState, valueToRemove) => {
      const nextCloudAccounts = removeSelectedValue(
        queryState.cloudAccounts,
        valueToRemove
      );
      return {
        cloudAccounts: nextCloudAccounts.length ? nextCloudAccounts : undefined,
        excludeCloudAccounts:
          nextCloudAccounts.length > 0
            ? queryState.excludeCloudAccounts
            : undefined,
      };
    },
  },
  {
    key: "resourceGroups",
    label: "Resource Group",
    getValues: (queryState) => queryState.resourceGroups ?? [],
    getValueLabel: (value) => resourceGroupNamesById[value] ?? value,
    getValueDisplay: (queryState, values) => {
      if (values.length === 1) {
        const label = resourceGroupNamesById[values[0]] ?? values[0];
        return queryState.excludeResourceGroups ? `Exclude: ${label}` : label;
      }

      return getCountBasedDisplayValue(
        values,
        queryState.excludeResourceGroups,
        {
          singular: "Resource Group",
          plural: "Resource Groups",
        }
      );
    },
    clear: () => ({
      resourceGroups: undefined,
      excludeResourceGroups: undefined,
    }),
    removeValue: (queryState, valueToRemove) => {
      const nextResourceGroups = removeSelectedValue(
        queryState.resourceGroups,
        valueToRemove
      );
      return {
        resourceGroups: nextResourceGroups.length
          ? nextResourceGroups
          : undefined,
        excludeResourceGroups:
          nextResourceGroups.length > 0
            ? queryState.excludeResourceGroups
            : undefined,
      };
    },
  },
  createCountFilterDefinition<ResourceQueryState, "resourcePingStatuses">({
    key: "resourcePingStatuses",
    label: "Ping Status",
    labels: { singular: "Ping Status", plural: "Ping Statuses" },
    getValues: (queryState) => queryState.resourcePingStatuses,
    getExcluded: (queryState) => queryState.excludeResourcePingStatuses,
    clear: () => ({
      resourcePingStatuses: undefined,
      excludeResourcePingStatuses: undefined,
    }),
    applyValuesPatch: (queryState, values) => ({
      resourcePingStatuses: values.length ? values : undefined,
      excludeResourcePingStatuses: values.length
        ? queryState.excludeResourcePingStatuses
        : undefined,
    }),
  }),
  {
    key: "lastUpdateStatuses",
    label: "Last Update Status",
    getValues: (queryState) => (queryState.updateStatus as string[]) ?? [],
    getValueLabel: (value) => lastUpdateStatusLabelsByValue[value] ?? value,
    getValueDisplay: (queryState, values) => {
      if (values.length === 1) {
        const label = lastUpdateStatusLabelsByValue[values[0]] ?? values[0];
        return queryState.excludeUpdateStatus ? `Exclude: ${label}` : label;
      }

      return getCountBasedDisplayValue(values, queryState.excludeUpdateStatus, {
        singular: "Last Update Status",
        plural: "Last Update Statuses",
      });
    },
    clear: () => ({ updateStatus: undefined, excludeUpdateStatus: undefined }),
    removeValue: (queryState, valueToRemove) => {
      const nextUpdateStatuses = removeSelectedValue(
        queryState.updateStatus as string[] | undefined,
        valueToRemove
      );
      return {
        updateStatus: nextUpdateStatuses.length
          ? (nextUpdateStatuses as ResourceQuery["updateStatus"])
          : undefined,
        excludeUpdateStatus: nextUpdateStatuses.length
          ? queryState.excludeUpdateStatus
          : undefined,
      };
    },
  },
  {
    key: "operatingSystems",
    label: "Operating System",
    getValues: (queryState) => [
      ...(queryState.operatingSystems ?? []),
      ...(queryState.operatingSystemSubTypes ?? []),
    ],
    getValueLabel: (value) => operatingSystemLabelsByValue[value] ?? value,
    getValueDisplay: (queryState, values) => {
      if (values.length === 1) {
        const label = operatingSystemLabelsByValue[values[0]] ?? values[0];
        return queryState.excludeOperatingSystems ? `Exclude: ${label}` : label;
      }

      return getCountBasedDisplayValue(
        values,
        queryState.excludeOperatingSystems,
        {
          singular: "Operating System",
          plural: "Operating Systems",
        }
      );
    },
    clear: () => ({
      operatingSystems: undefined,
      operatingSystemSubTypes: undefined,
      excludeOperatingSystems: undefined,
    }),
    removeValue: (queryState, valueToRemove) => {
      const nextOperatingSystems = removeSelectedValue(
        queryState.operatingSystems,
        valueToRemove
      );
      const nextOperatingSystemSubTypes = removeSelectedValue(
        queryState.operatingSystemSubTypes,
        valueToRemove
      );
      const hasAnySelection =
        nextOperatingSystems.length > 0 ||
        nextOperatingSystemSubTypes.length > 0;

      return {
        operatingSystems: nextOperatingSystems.length
          ? nextOperatingSystems
          : undefined,
        operatingSystemSubTypes: nextOperatingSystemSubTypes.length
          ? nextOperatingSystemSubTypes
          : undefined,
        excludeOperatingSystems: hasAnySelection
          ? queryState.excludeOperatingSystems
          : undefined,
      };
    },
  },
  createCountFilterDefinition<ResourceQueryState, "resourceTags">({
    key: "resourceTags",
    label: "Resource Tags",
    labels: { singular: "Resource Tag", plural: "Resource Tags" },
    getValues: (queryState) => queryState.resourceTagKeysAndTagValues,
    getExcluded: (queryState) => queryState.excludeResourceTagKeysAndTagValues,
    clear: () => ({
      resourceTagKeysAndTagValues: undefined,
      excludeResourceTagKeysAndTagValues: undefined,
    }),
    applyValuesPatch: (queryState, values) => ({
      resourceTagKeysAndTagValues: values.length ? values : undefined,
      excludeResourceTagKeysAndTagValues: values.length
        ? queryState.excludeResourceTagKeysAndTagValues
        : undefined,
    }),
  }),
];
